/**
 * The way through a vulnerability case as navigation (DEV-103).
 *
 * `CaseNav`: one list beside the content, in one style, with two groups —
 * „Meldungen an die Behörde“ (each report stage with its deadline chip) and
 * „Ablauf“ (1 Kernangaben → 2 Security Advisory → 3 Kundenmails). The numbered
 * badge tells the state of a step: green done (with a check), orange next,
 * grey open or locked (the reason beneath). The open entry is set off quietly:
 * grey ground, bold.
 *
 * `CaseStepsLine`: the same path as a quiet text line below a product verdict.
 */
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { msg, t as translate } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import type { ReportStage, VulnerabilityCase } from '../../lib/types';

import { clockChip } from './CasePanel';
import { caseSteps, type CaseSection, type CaseStep } from './caseSteps';
import { backendLabel, shortBackendLabel } from '../../lib/backendLabels';

const STATE_LABELS = { done: msg`erledigt`, next: msg`nächster Schritt`, open: msg`offen`, locked: msg`gesperrt` } as const;

/** The report to the authority that runs out next, or null when none is running. */
function nextReport(item: VulnerabilityCase): VulnerabilityCase['clocks'][number] | null {
  const open = item.clocks.filter((clock) => !clock.submitted_at && clock.due);
  open.sort((a, b) => (a.due ?? '').localeCompare(b.due ?? ''));
  return open[0] ?? null;
}

/** Accessible name of a step: number, label, state and detail in one sentence. */
function stepLabel(step: CaseStep): string {
  const { number, label, detail } = step;
  const state = translate(STATE_LABELS[step.state]);
  return translate`${number}. ${label}: ${state}, ${detail}`;
}

function reportsDetail(item: VulnerabilityCase): string {
  return item.clocks.every((clock) => clock.submitted_at) ? translate`alle gemeldet` : translate`wartet auf die Abhilfemaßnahme`;
}

export function CaseNav({ item, section, stage, unsaved, header = null, onSection, onReport }: Readonly<{
  item: VulnerabilityCase;
  section: CaseSection;
  stage: ReportStage;
  /** Work areas with unsaved edits. */
  unsaved: Partial<Record<CaseSection, boolean>>;
  /** Above the groups, e.g. the choice of the case when a hit has several. */
  header?: ReactNode;
  onSection: (section: CaseSection) => void;
  /** Opens „Meldungen“ at this stage. */
  onReport: (stage: ReportStage) => void;
}>): ReactNode {
  const { t } = useLingui();
  return (
    <nav className="case-nav" aria-label={t`Schwachstellenfall`}>
      {header}
      <h4 className="case-nav-group"><Trans>Meldungen an die Behörde</Trans></h4>
      <ul className="case-nav-list">
        {item.clocks.map((clock) => {
          const chip = clockChip(clock);
          const active = section === 'reports' && stage === clock.stage;
          return (
            <li key={clock.key}>
              <button type="button" className={`case-nav-item${active ? ' on' : ''}`} aria-current={active ? 'page' : undefined}
                title={backendLabel(clock.label)} onClick={() => onReport(clock.stage)}>
                <span className="case-nav-label">{shortBackendLabel(clock.label)}</span>
                <Chip tone={chip.tone}>{clock.state === 'waiting' ? t`wartet` : chip.label}</Chip>
              </button>
            </li>
          );
        })}
      </ul>
      <h4 className="case-nav-group"><Trans>Ablauf</Trans></h4>
      <ol className="case-nav-list">
        {caseSteps(item).map((step) => {
          const active = section === step.section;
          const { detail } = step;
          return (
            <li key={step.section}>
              <button type="button" className={`case-nav-item ${step.state}${active ? ' on' : ''}`}
                aria-current={active ? 'page' : undefined}
                aria-label={stepLabel(step)}
                onClick={() => onSection(step.section)}>
                <span className="case-step-number small" aria-hidden="true">{step.number}</span>
                <span className="case-nav-text">
                  <span className="case-nav-label">
                    {step.label}
                    {step.state === 'done' ? <Icon name="check" size={14} className="case-step-check" /> : null}
                  </span>
                  <span className="case-step-detail">
                    {unsaved[step.section] ? t`${detail} · ungespeichert` : detail}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Below a product verdict: the path as one quiet line, the next deadline beneath. */
export function CaseStepsLine({ item, onSelect }: Readonly<{ item: VulnerabilityCase; onSelect: (section: CaseSection) => void }>): ReactNode {
  const { t } = useLingui();
  const next = nextReport(item);
  const chip = next ? clockChip(next) : null;
  return (
    <nav className="case-path" aria-label={t`Schritte des Schwachstellenfalls`}>
      <ol className="case-path-steps">
        {caseSteps(item).map((step) => (
          <li key={step.section}>
            <button type="button" className={`case-path-step ${step.state}`} onClick={() => onSelect(step.section)}
              aria-label={stepLabel(step)}>
              <span className="case-step-number small" aria-hidden="true">{step.number}</span>
              {step.label}
              {step.state === 'done'
                ? <Icon name="check" size={14} className="case-step-check" />
                : <span className="case-step-detail">{step.detail}</span>}
            </button>
          </li>
        ))}
      </ol>
      <button type="button" className="case-path-step" onClick={() => onSelect('reports')}>
        <Trans>Meldungen an die Behörde:</Trans>
        {chip && next ? <> {shortBackendLabel(next.label)} <Chip tone={chip.tone}>{chip.label}</Chip></>
          : <span className="case-step-detail">{reportsDetail(item)}</span>}
      </button>
    </nav>
  );
}

